import { ArrowDownRight, ArrowUpRight } from "@phosphor-icons/react/dist/ssr";
import type { ReactNode } from "react";
import { priceParts } from "@/lib/price-utils";

/**
 * Shared layout for a service-package card.
 *
 * Three surfaces render the same card and must not drift: the interactive
 * `PackageCard` (signed-in profile view, tracks a package_click lead), the
 * profile page's logged-out peek preview, and the admin applicant preview
 * shown before approval. They had diverged — the peek rendered an empty
 * `<p>` for a blank description, and the admin copy stacked the price
 * differently — which defeats the point of a preview that is supposed to
 * show what will actually be published.
 *
 * Deliberately hook-free and label-injected rather than calling
 * `useTranslations` itself: the peek and admin previews are server
 * components while `PackageCard` is a client component, and the admin panel
 * is intentionally English-only (see the i18n section in CLAUDE.md), so it
 * passes literals where the public surfaces pass `t(…)`.
 */

/** Wrapper classes. Stacks on mobile so a long description isn't squeezed
 *  into a narrow column beside the price; side-by-side from `sm` up. */
export const PACKAGE_CARD_CLASS =
  "flex flex-col items-start gap-13 rounded-card border border-card-line bg-white p-21 shadow-e1 sm:flex-row sm:justify-between sm:gap-21";

export interface PackageLabels {
  /** Prefix for a numeric amount — the price is a starting point, not the price. */
  from: string;
  free: string;
  /** Shown when the pro left the price blank OR hides prices publicly —
   *  both read the same to a visitor. */
  contactForPrice: string;
}

export interface PackageCardPkg {
  name: string;
  description?: string | null;
  price: string;
}

/** What to SHOW for the name and description when they differ from the
 *  stored text (a translated profile). The stored name still drives events
 *  and the contact form, so the professional receives their own words. */
export interface PackageDisplay {
  name: ReactNode;
  description?: ReactNode;
}

/** Builds the same text the card shows, for an `aria-label` on the
 *  interactive variant — so the accessible name matches the visible one. */
export function packagePriceLabel(price: string, labels: PackageLabels): string {
  const { formatted, isNumeric, isFree } = priceParts(price);
  if (isFree) return labels.free;
  if (isNumeric) return `${labels.from} ${formatted}`;
  return formatted || labels.contactForPrice;
}

/** Resting position and transition shared by both arrow directions. */
const ARROW_MOTION =
  "translate-x-0 translate-y-0 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out group-hover:translate-x-2 group-focus-visible:translate-x-2";

export default function PackageCardContent({
  pkg,
  labels,
  interactive = false,
  display,
}: {
  pkg: PackageCardPkg;
  display?: PackageDisplay;
  labels: PackageLabels;
  /** True only for the clickable `PackageCard`. The arrow after "Contact for
   *  price" promises that the card leads to the contact form, so the two
   *  previews, which are plain divs, must not show it (review, 2026-09-13). */
  interactive?: boolean;
}) {
  const { formatted, isNumeric, isFree } = priceParts(pkg.price);
  return (
    <>
      <div>
        <h3 className="font-serif text-lg text-navy">{display?.name ?? pkg.name}</h3>
        {pkg.description && (
          <p className="mt-3 text-sm text-gray-500">{display?.description ?? pkg.description}</p>
        )}
      </div>
      {isFree ? (
        <span className="shrink-0 font-serif text-2xl leading-tight text-navy">
          {labels.free}
        </span>
      ) : formatted ? (
        <span className="flex shrink-0 flex-col items-start font-serif text-2xl leading-tight text-navy">
          {/* Casing lives in the message value, not a `capitalize` utility —
              the utility left the visible "From" disagreeing with the raw
              lowercase string that `packagePriceLabel` puts in the aria-label,
              and it would capitalise every word of a multi-word translation. */}
          {isNumeric && (
            <span className="font-sans text-xs font-normal text-gray-500">
              {labels.from}
            </span>
          )}
          {formatted}
        </span>
      ) : (
        // Navy and semibold like a real price (owner, 2026-09-13): in grey it
        // read as a footnote, when it is the answer to "what does this cost".
        // The arrow says the card leads somewhere (the contact form, with this
        // package selected). `/dist/ssr` because two of the three surfaces
        // rendering this card are server components.
        <span className="inline-flex shrink-0 items-center gap-5 text-sm font-semibold text-navy">
          {labels.contactForPrice}
          {interactive && (
            <>
              {/* The arrow points where the contact form IS: below the packages
                  under `lg`, where the profile stacks, and in the right-hand column
                  from `lg` up (the profile's `lg:grid-cols-[1fr_380px]`). It nudges
                  the way it points on hover or keyboard focus, keyed on the
                  card's `group`. motion-safe, because the global
                  reduced-motion rule covers animations, not transitions; the
                  explicit resting translate is the Tailwind v4 trap in CLAUDE.md,
                  where a transition from `none` can snap instead of interpolating. */}
              <ArrowDownRight
                size={14}
                weight="bold"
                aria-hidden="true"
                className={`${ARROW_MOTION} lg:hidden group-hover:translate-y-2 group-focus-visible:translate-y-2`}
              />
              <ArrowUpRight
                size={14}
                weight="bold"
                aria-hidden="true"
                className={`${ARROW_MOTION} hidden lg:block group-hover:-translate-y-2 group-focus-visible:-translate-y-2`}
              />
            </>
          )}
        </span>
      )}
    </>
  );
}
